// src/layouts/AdminProtectedLayout.jsx
import React, { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/useAppDispatch.js'
import { validateToken, logout, initializeAuth } from '../redux/slices/authSlice.js'
import AdminLayout from './AdminLayout.jsx'
import { Toaster } from 'react-hot-toast'
import { setNavigate } from '../services/apiClient.js'

export default function AdminProtectedLayout() {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { isAuthenticated, user, loading, initialized } = useAppSelector((state) => state.auth)
    const [authChecked, setAuthChecked] = useState(false)
    const from = location.state?.from?.pathname || '/admin/cms'

    // Set navigate in apiClient for global access
    useEffect(() => {
        setNavigate(navigate)
    }, [navigate])

    // Initialize auth state on mount
    useEffect(() => {
        dispatch(initializeAuth())
    }, [dispatch])

    // Check authentication on mount and location change
    useEffect(() => {
        const checkAuthentication = async () => {
            const token = localStorage.getItem('token')
            const userStr = localStorage.getItem('user')

            // Case 1: No token at all
            if (!token || !userStr) {
                setAuthChecked(true)
                return
            }

            // Case 2: Has token but Redux state isn't initialized yet
            if (!initialized) {
                try {
                    // Parse user from localStorage first for immediate UI
                    let parsedUser = null
                    try {
                        parsedUser = userStr ? JSON.parse(userStr) : null
                    } catch (e) {
                        console.error('Failed to parse user from localStorage:', e)
                        localStorage.removeItem('user')
                        localStorage.removeItem('token')
                        setAuthChecked(true)
                        return
                    }

                    // Validate token with server
                    await dispatch(validateToken()).unwrap()

                    // Check if user is admin
                    if (parsedUser?.role !== 'admin') {
                        dispatch(logout())
                    }
                } catch (error) {
                    // Token validation failed
                    console.error('Token validation failed:', error)
                } finally {
                    setAuthChecked(true)
                }
            } else {
                setAuthChecked(true)
            }
        }

        checkAuthentication()
    }, [dispatch, initialized, location.pathname])

    // Show loading during initial auth check
    if (!authChecked || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Toaster position="top-right" />
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Verifying authentication...</p>
                </div>
            </div>
        )
    }

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
        return (
            <>
                <Toaster position="top-right" />
                <Navigate to="/admin/login" state={{ from: location }} replace />
            </>
        )
    }

    // If authenticated but not admin, redirect to login with toast
    if (user?.role !== 'admin') {
        dispatch(logout())
        return (
            <>
                <Toaster position="top-right" />
                <Navigate to="/admin/login" state={{ from: location }} replace />
            </>
        )
    }

    // User is authenticated and is admin
    return (
        <>
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#10B981',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#EF4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
            <AdminLayout>
                <Outlet />
            </AdminLayout>
        </>
    )
}